import { useState, type FormEvent } from 'react';
import type { PortfolioData } from '../../firebase/types/portfolio.types';
import { digits } from './model';
import { Band, SectionIndex } from './ui';

export interface ContactFormPayload {
  name: string;
  email: string;
  message: string;
}

function ContactRow({ href, label, value, external }: { href: string; label: string; value: string; external?: boolean }) {
  return (
    <a
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="flex min-h-12 items-center justify-between gap-4 border-b border-line py-3 text-left"
    >
      <span className="shrink-0 text-sm text-faint">{label}</span>
      <span className="min-w-0 break-all text-right text-sm text-ink md:text-base">{value}</span>
    </a>
  );
}

export default function ContactSection({
  data,
  index,
  onSubmitContact,
}: {
  data: PortfolioData;
  index: string;
  onSubmitContact?: (payload: ContactFormPayload) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const whatsappNumber = digits(data.whatsapp) || digits(data.phone);
  const whatsappUrl = whatsappNumber ? `https://wa.me/${whatsappNumber}` : null;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!onSubmitContact || !name.trim() || !email.trim() || !message.trim()) return;
    setStatus('sending');
    try {
      await onSubmitContact({ name: name.trim(), email: email.trim(), message: message.trim() });
      setStatus('success');
      setName('');
      setEmail('');
      setMessage('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <Band id="contact">
      <div className="grid gap-8 lg:grid-cols-12 lg:gap-10">
        <div className={onSubmitContact ? 'lg:col-span-5' : 'lg:col-span-12 max-w-3xl'}>
          <SectionIndex index={index} title="Contact" text={data.contactCta?.trim()} />
          {data.calendarBookingUrl?.trim() ? (
            <a
              href={data.calendarBookingUrl.trim()}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary mb-5"
            >
              Book a call
            </a>
          ) : null}
          <div className="border-t border-line">
            {data.email ? <ContactRow href={`mailto:${data.email}`} label="Email" value={data.email} /> : null}
            {data.phone ? <ContactRow href={`tel:${digits(data.phone)}`} label="Phone" value={data.phone} /> : null}
            {whatsappUrl ? (
              <ContactRow href={whatsappUrl} label="WhatsApp" value={data.whatsapp || data.phone || 'WhatsApp'} external />
            ) : null}
            {data.linkedin ? <ContactRow href={data.linkedin} label="LinkedIn" value="Profile" external /> : null}
            {data.github ? <ContactRow href={data.github} label="GitHub" value="Profile" external /> : null}
            {data.portfolioUrl ? <ContactRow href={data.portfolioUrl} label="Website" value={data.portfolioUrl} external /> : null}
            {data.location ? (
              <p className="flex min-h-12 items-center justify-between gap-4 border-b border-line py-3">
                <span className="text-sm text-faint">Location</span>
                <span className="text-right text-sm text-ink md:text-base">{data.location}</span>
              </p>
            ) : null}
          </div>
        </div>
        {onSubmitContact ? (
          <form onSubmit={handleSubmit} className="panel h-fit p-5 sm:p-8 lg:col-span-7">
            <div className="space-y-5">
              <label className="block">
                <span className="mb-2 block text-sm text-muted">Name</span>
                <input
                  className="field"
                  name="name"
                  autoComplete="name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm text-muted">Email</span>
                <input
                  className="field"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-sm text-muted">Message</span>
                <textarea
                  className="field"
                  name="message"
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  required
                />
              </label>
            </div>
            <button type="submit" className="btn btn-primary mt-6 w-full sm:w-auto" disabled={status === 'sending'}>
              {status === 'sending' ? 'Sending…' : 'Send message'}
            </button>
            {status === 'success' ? (
              <p role="status" className="mt-4 text-sm text-ok">
                Message sent. I&apos;ll get back to you soon.
              </p>
            ) : null}
            {status === 'error' ? (
              <p role="status" className="mt-4 text-sm text-danger">
                Something went wrong. Please try again or email directly.
              </p>
            ) : null}
          </form>
        ) : null}
      </div>
    </Band>
  );
}
